// Atlas intermediate city model (ATL-1/2). PURE (ENG-1).
//
// A compact, serializable description of a real (or synthesized) city in BLOCK
// coordinates — the boundary between the fetch/synthesis side (Overpass parser or
// the fallback) and the pure, isomorphic rasterizer (rasterize.ts / GEN-1). All
// coordinates are integer-ish block XY (x east, z south), already projected from
// lat/lon ENU and divided by the meters-per-block scale. Polygons are closed rings.
import { z } from 'zod';

export const ROAD_CLASSES = ['motorway', 'primary', 'secondary', 'residential', 'footway'] as const;
export type RoadClass = (typeof ROAD_CLASSES)[number];

/** Road stroke widths in METERS by class (ATL-3). Divided by `scale` → blocks. */
export const ROAD_WIDTH_M: Record<RoadClass, number> = {
  motorway: 12, primary: 9, secondary: 7, residential: 5, footway: 2,
};

const point2 = z.tuple([z.number(), z.number()]);

export const polylineSchema = z.strictObject({
  points: z.array(point2).min(2),
  roadClass: z.enum(ROAD_CLASSES),
});
export const polygonSchema = z.strictObject({
  points: z.array(point2).min(3),
  /** Optional inner rings (courtyards / lakes) subtracted via even-odd (E-20). */
  holes: z.array(z.array(point2).min(3)).optional(),
});
export const buildingSchema = z.strictObject({
  footprint: z.array(point2).min(3),
  levels: z.number().int().min(1).max(64).default(2),
  /** Interior courtyards subtracted from the footprint (E-20). */
  holes: z.array(z.array(point2).min(3)).optional(),
});

/** OSM aeroway kinds we voxelize (AVI-1). */
export const AEROWAY_KINDS = ['runway', 'taxiway', 'apron', 'helipad'] as const;
export type AerowayKind = (typeof AEROWAY_KINDS)[number];

/** A runway/taxiway (centerline polyline) or apron/helipad (boundary ring) in block
 *  coordinates (AVI-1). Runways are flattened to a single y so they are landable. */
export const aerowaySchema = z.strictObject({
  kind: z.enum(AEROWAY_KINDS),
  /** Centerline for runway/taxiway (≥2 pts); boundary ring for apron/helipad. */
  points: z.array(point2).min(2),
  /** Width in metres (OSM `width` tag); divided by `scale` → blocks. */
  widthM: z.number().positive().optional(),
  /** Runway designator, e.g. "2/20" (threshold/label paint). */
  ref: z.string().optional(),
});

export const intermediateCitySchema = z.strictObject({
  version: z.literal(1),
  /** Meters per block. */
  scale: z.number().positive(),
  /** Half-extent of the generated area, in blocks (city spans ±sizeBlocks). */
  sizeBlocks: z.number().int().positive(),
  /** True when produced by the fallback synthesizer (UI "simulated" badge, ATL-2). */
  simulated: z.boolean().default(false),
  label: z.string().default(''),
  roads: z.array(polylineSchema).default([]),
  water: z.array(polygonSchema).default([]),
  green: z.array(polygonSchema).default([]),
  buildings: z.array(buildingSchema).default([]),
  /** Explicit tree points (OSM natural=tree); parks add Poisson trees (ATL-4). */
  trees: z.array(point2).default([]),
  /** Runways / taxiways / aprons (AVI-1); default [] keeps non-aviation worlds
   *  byte-identical (the aeroway raster is inert when empty). */
  aeroways: z.array(aerowaySchema).default([]),
});

export type Polyline = z.infer<typeof polylineSchema>;
export type Polygon = z.infer<typeof polygonSchema>;
export type Building = z.infer<typeof buildingSchema>;
export type Aeroway = z.infer<typeof aerowaySchema>;
export type IntermediateCity = z.infer<typeof intermediateCitySchema>;

export type Pt = readonly [number, number];

/** Even-odd point-in-polygon (E-19: self-intersecting rings fill by parity), with
 *  optional holes (E-20). A point inside an odd number of rings is inside. */
export function pointInPolygon(x: number, z: number, ring: readonly Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, zi] = ring[i]!, [xj, zj] = ring[j]!;
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

/** Point inside a polygon honoring holes (courtyards): inside outer, not in a hole. */
export function pointInPolygonWithHoles(x: number, z: number, poly: Polygon): boolean {
  if (!pointInPolygon(x, z, poly.points)) return false;
  if (poly.holes) for (const h of poly.holes) if (pointInPolygon(x, z, h)) return false;
  return true;
}

/** Squared distance from (px,pz) to segment (ax,az)-(bx,bz). */
export function distToSegmentSq(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax, dz = bz - az;
  const len2 = dx * dx + dz * dz;
  let t = len2 === 0 ? 0 : ((px - ax) * dx + (pz - az) * dz) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx, cz = az + t * dz;
  return (px - cx) ** 2 + (pz - cz) ** 2;
}

export interface BBox { minX: number; minZ: number; maxX: number; maxZ: number; }

/** Axis-aligned bounds of a point set (empty → an inverted box). */
export function boundsOf(points: readonly Pt[]): BBox {
  let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
  for (const [x, z] of points) {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }
  return { minX, minZ, maxX, maxZ };
}

export function bboxIntersects(a: BBox, b: BBox): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minZ <= b.maxZ && a.maxZ >= b.minZ;
}

/** FNV-1a hash of a string → uint32 (fallback seed, ATL-2). */
export function fnv1a(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619) >>> 0;
  return h >>> 0;
}
